// src/pages/portal/Page_Welcome/PageWelcome.js
//
// Fase 1A Milestone 5 - K12 Free Refactor
//
// Onboarding 3 pasos para usuarios free recien registrados. Solo se
// muestra UNA VEZ tras el alta:
//   - registerPending + verifyRegistration -> RegisterForm redirige
//   - Login Google con isNewUser=true -> RegisterForm redirige
//
// En logins posteriores el usuario va directo a /portal/teacher/salas.
// No persistimos "ya visto" en BD; basta con el flag isNewUser que
// devuelve el backend en el alta.

import React, { useState } from "react";
import { Button } from "semantic-ui-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks";
import "./PageWelcome.scss";

const TOTAL_STEPS = 3;

export function PageWelcome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [step, setStep] = useState(1);

  const firstname = user?.user?.firstname || "";

  const goNext = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  const goBack = () => setStep((s) => Math.max(s - 1, 1));
  const finish = () => navigate("/portal/teacher/salas", { replace: true });

  return (
    <div className="page-welcome">
      <div className="page-welcome__card">
        <div className="page-welcome__progress">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <span
              key={i}
              className={`page-welcome__dot ${i + 1 <= step ? "is-active" : ""}`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="page-welcome__step">
            <h1>
              {t("welcome.step1.title", { defaultValue: "Bienvenido" })}
              {firstname ? `, ${firstname}` : ""}
            </h1>
            <p className="page-welcome__lead">
              {t("welcome.step1.subtitle", {
                defaultValue:
                  "Has creado tu cuenta gratuita Educa360. Disponible durante 3 meses con 1 alumno de prueba.",
              })}
            </p>
            <div className="page-welcome__info">
              <p>
                <strong>
                  {t("welcome.step1.emailTitle", {
                    defaultValue: "Revisa tu email",
                  })}
                </strong>
              </p>
              <p>
                {t("welcome.step1.emailBody", {
                  defaultValue:
                    "Te hemos enviado las credenciales de acceso de tu alumno de prueba. Usalas para probar la plataforma desde la perspectiva del estudiante.",
                })}
              </p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="page-welcome__step">
            <h1>
              {t("welcome.step2.title", {
                defaultValue: "Esto es lo que puedes hacer",
              })}
            </h1>
            <ul className="page-welcome__tour">
              <li>
                <strong>
                  {t("welcome.step2.scenes.title", {
                    defaultValue: "Escenas VR",
                  })}
                </strong>
                <br />
                {t("welcome.step2.scenes.body", {
                  defaultValue:
                    "Explora el catalogo de escenas inmersivas y crea itinerarios didacticos para tus sesiones.",
                })}
              </li>
              <li>
                <strong>
                  {t("welcome.step2.ai.title", {
                    defaultValue: "Asistentes IA",
                  })}
                </strong>
                <br />
                {t("welcome.step2.ai.body", {
                  defaultValue:
                    "Conversa con asistentes especializados sobre cualquier tema del curriculo.",
                })}
              </li>
              <li>
                <strong>
                  {t("welcome.step2.evaluations.title", {
                    defaultValue: "Evaluaciones",
                  })}
                </strong>
                <br />
                {t("welcome.step2.evaluations.body", {
                  defaultValue:
                    "Construye quizzes y aplicalos a tus alumnos en tiempo real.",
                })}
              </li>
            </ul>
          </div>
        )}

        {step === 3 && (
          <div className="page-welcome__step">
            <h1>
              {t("welcome.step3.title", {
                defaultValue: "Empieza por tu primera escena",
              })}
            </h1>
            <p className="page-welcome__lead">
              {t("welcome.step3.subtitle", {
                defaultValue:
                  "El catalogo VR esta abierto. Elige una escena, observa una vista previa o entra directamente al aula virtual.",
              })}
            </p>
          </div>
        )}

        <div className="page-welcome__actions">
          {step > 1 && (
            <Button basic onClick={goBack}>
              {t("welcome.back", { defaultValue: "Atras" })}
            </Button>
          )}
          {step < TOTAL_STEPS ? (
            <Button primary onClick={goNext}>
              {t("welcome.next", { defaultValue: "Siguiente" })}
            </Button>
          ) : (
            <Button primary onClick={finish}>
              {t("welcome.finish", { defaultValue: "Entrar al catalogo" })}
            </Button>
          )}
        </div>

        <button type="button" className="page-welcome__skip" onClick={finish}>
          {t("welcome.skip", { defaultValue: "Saltar onboarding" })}
        </button>
      </div>
    </div>
  );
}
